import React from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { MAX_LENGTH_DESCRIPTION_TEXT } from "shared/constants/app-constants"
import { WithClassName } from "shared/types/WithClassName"
import { MarkdownInput } from "shared/ui/input/markdown/MarkdownInput"
import { HeadingLevel } from "shared/ui/input/markdown/components/MarkdownEditor"

import { ProjectFormData } from "features/project-management/types/ProjectFormData"

import { useProjectFormContext } from "../contexts/ProjectFormContext"

const allowedHeadingLevels: HeadingLevel[] = [1, 2, 3]

export interface ProjectFormDescriptionProps extends WithClassName {
  onExpand?: (isExpanded: boolean) => void
  hideLabel?: boolean
}

export const ProjectFormDescription = React.memo(
  ({ onExpand, hideLabel, className }: ProjectFormDescriptionProps) => {
    const { t } = useTranslation()
    const { isReadonly } = useProjectFormContext()
    const { watch } = useFormContext<ProjectFormData>()
    const description = watch("project.description")

    if (isReadonly && !description) {
      return (
        <div
          className={classNames(
            "project-description",
            "project-description--empty",
            className
          )}
        />
      )
    }

    if (isReadonly && description) {
      return (
        <MarkdownInput.Root
          label={t("General.Remark")}
          maxLength={MAX_LENGTH_DESCRIPTION_TEXT}
          className={classNames("project-description", className)}
          readonly={isReadonly}
        >
          {hideLabel ? null : (
            <MarkdownInput.Label>{t("General.Remark")}</MarkdownInput.Label>
          )}
          <MarkdownInput.Container
            maxHeight={240}
            threshold={60}
            onExpand={onExpand}
          >
            <MarkdownInput.Readonly value={description || ""} />
          </MarkdownInput.Container>
        </MarkdownInput.Root>
      )
    }

    return (
      <MarkdownInput.Root
        label={t("General.Remark")}
        maxLength={MAX_LENGTH_DESCRIPTION_TEXT}
        name="project.description"
        className={classNames("project-description", className)}
        readonly={isReadonly}
      >
        {hideLabel ? null : (
          <MarkdownInput.Label>{t("General.Remark")}</MarkdownInput.Label>
        )}
        <MarkdownInput.Container
          maxHeight={240}
          threshold={60}
          onExpand={onExpand}
        >
          <MarkdownInput.Input allowedHeadingLevels={allowedHeadingLevels} />
          <MarkdownInput.Counter />
          <MarkdownInput.Readonly value={description || ""} />
        </MarkdownInput.Container>
      </MarkdownInput.Root>
    )
  }
)

ProjectFormDescription.displayName = "ProjectFormDescription"
